import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { ButtonModel, ModalContext } from '@notino/react-styleguide';

import { useWithSpaRedirect } from '@components/ClientRedirect/useHandleClientRedirect';
import PriceLabel from '@components/PriceLabel';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import {
  useGetMasterAddToCart,
  ProductEventWither,
  TrackedProduct,
} from '@helpers/googleTagManager';
import { AddToCartButtonWithoutAddToCartModal } from '@sharedComponents/AddToCartButton/AddToCartButtonWithoutAddToCartModal';

import { GTM_LIST_LABEL } from '../../constants';
import { getUpsellingData_vpProductByIds } from '../queries/__generated__/getUpsellingData';

import {
  Annotation,
  ProductTileWrapper,
  Image,
  ImageWrapper,
  BrandTitle,
  Price,
  PriceWrapper,
  CurrencyWrapper,
  DescriptionSectionWrapper,
  ButtonWrapper,
  ProductTileAnchor,
  ProductName,
  StyledSpecialSalesIcon,
} from './styled';

export interface ProductTitleOwnProps {
  position: number;
  productById: getUpsellingData_vpProductByIds;
  trackVisibleItemsGTM: (product: TrackedProduct) => void;
}

export const ProductTile: React.FC<ProductTitleOwnProps> = React.memo(
  ({ productById, position, trackVisibleItemsGTM }) => {
    const {
      imageUrl,
      brand,
      name,
      url,
      price,
      annotation,
      id,
      productCode,
      attributes,
      masterId,
      canBuy,
      stockAvailability,
    } = productById;

    const [ref, inView] = useInView();
    const { hideModal } = ModalContext.useModalContext();
    const withSpaRedirect = useWithSpaRedirect(productById);

    const trackSelectItem = () => {
      dispatchTrackingEvent({
        event: 'select_item',
        // todo: remove this when release new version of web-tracking with updated types
        // @ts-ignore
        product: [
          ProductEventWither()
            .withVpProduct({
              ...productById,
              reviewOverview: null,
            })
            .withAdditionalData({
              list_position: position,
              list_name: GTM_LIST_LABEL,
            })
            .build(),
        ],
        _clear: true,
      });
    };

    const onClick = withSpaRedirect(() => {
      hideModal();
      trackSelectItem();
    });

    const trackAddToCart = useGetMasterAddToCart(GTM_LIST_LABEL);

    const onProductAdded = async () => {
      await trackAddToCart(productById, masterId.toString(), position);
    };

    React.useEffect(() => {
      if (inView) {
        trackVisibleItemsGTM(
          ProductEventWither()
            .withVpProduct({
              ...productById,
              reviewOverview: null,
            })
            .withAdditionalData({
              list_position: position,
              id: productById.id,
            })
            .build()
        );
      }
    }, [inView, productById, position, trackVisibleItemsGTM]);

    return (
      <ProductTileWrapper
        data-testid={'upselling-product'}
        id={`upsellingProduct-${id}`}
        ref={ref}
      >
        <ProductTileAnchor href={url} onClick={onClick}>
          <ImageWrapper>
            <Image
              src={imageUrl}
              alt={`${brand?.name} ${name}`}
              sourceKey="list"
            />

            <StyledSpecialSalesIcon
              attributes={attributes}
              stockAvailabilityCode={stockAvailability.code}
            />
          </ImageWrapper>

          <DescriptionSectionWrapper>
            <BrandTitle>{brand.name}</BrandTitle>
            <ProductName>{name}</ProductName>
            <Annotation>{annotation}</Annotation>
          </DescriptionSectionWrapper>

          <Price>
            <PriceLabel
              price={price}
              wrapperComponent={PriceWrapper}
              currencyComponent={CurrencyWrapper}
            />
          </Price>
        </ProductTileAnchor>

        {canBuy && (
          <AddToCartButtonWithoutAddToCartModal
            buttonWrapper={ButtonWrapper}
            buttonWrapperProps={{ 'data-code': productById.productCode }}
            buttonStyle={ButtonModel.Styles.secondary}
            buttonSize={ButtonModel.Sizes.xSmall}
            product={{
              id,
              attributes,
              productCode,
              name,
              variantName: annotation,
              masterId,
            }}
            count={1}
            disableRedirect={true}
            wrapable={true}
            onProductAdded={onProductAdded}
          />
        )}
      </ProductTileWrapper>
    );
  }
);
